import { AbstractControl, FormControl, FormGroupDirective, FormsModule, NgControl, NgForm, Validators } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, Self, ViewChild } from '@angular/core';
import { LookUpServiceAndValidation } from '../../../services/look-up-and-validation.service';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { InputType, TransformType } from '../../../models/enums.models';
import { profanity } from '../../../validators/swear-word.validator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { StateService } from '../../../services/state.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';

//https://www.youtube.com/watch?v=yWw7CYFIDVw
class InputErrorStateMatcher implements ErrorStateMatcher {
    constructor(private innerControl: NgControl, private stateService: StateService) {}

    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        if (!control) {
            return false;
        }

        if (this.stateService.formIsSubmitted) {
            control.markAsDirty();
            control.markAsTouched();
            control.updateValueAndValidity(); 
            
            return this.innerControl.invalid!;
        }

        if (form?.submitted === false) {
            //Ignore errors on pristine fields (e.g., on page load)
            if (control.pristine) {
                return this.errorsIgnoreRequired(this.innerControl.control!);
            }

            //Show errors if the field is touched (user interaction)
            if (control.touched) {
                return this.errorsIgnoreRequired(this.innerControl.control!);
            }

            return false;
        }

        return this.innerControl.invalid!;
    }

    errorsIgnoreRequired(control: AbstractControl | null): boolean {
        if (!control?.errors) {
            return false;
        }

        if (this.stateService.formIsSubmitted) {
            return true;
        }

        if (control.errors['required']) {
            return false;
        }

        return true;
    }
}

@Component({
    selector: 'minster-input',
    standalone: true,
    imports: [FormsModule, MatFormFieldModule, MatInputModule, MatIconModule, MatTooltipModule],
    templateUrl: './minster-input.component.html',
    styleUrl: './minster-input.component.css',
    providers: [{ provide: ControlBaseComponent, useExisting: MinsterInputComponent }]
})

export class MinsterInputComponent extends ControlBaseComponent<string> implements OnInit {
    @Input('type') _type: string = "text";
    @Input('inputType') public set inputTypeIn(value: any) { this._inputType = value; };

    _step?: string;
    _min?: number;
    _max?: number;

    _inputType: InputType = InputType.AlphaNumeric;
    _matcher: InputErrorStateMatcher;

    @Output('blur') $onBlur: EventEmitter<void> = new EventEmitter();
    @ViewChild('inputControl') _matInput: FormControl | undefined;

    constructor(
        @Self() public controlDir: NgControl,
        private lookUpService: LookUpServiceAndValidation,
        private toastr: ToastrService,
        private stateService: StateService
    ) {
        super(controlDir, stateService);
        this._matcher = new InputErrorStateMatcher(this.controlDir, this.stateService);
    }

    ngOnInit(): void {
        this.setup();

        const control = this.controlDir.control;
        let myValidators = [];

        myValidators.push(profanity);

        if (this._required && !this._disabled) {
            myValidators.push(Validators.required);
        }

        if (this._minLength) {
            myValidators.push(Validators.minLength(Number(this._minLength)));
        }

        if (this._maxLength) {
            myValidators.push(Validators.maxLength(Number(this._maxLength)));
        }

        switch (this._inputType) {
            case InputType.Numeric:
                this._type = 'number';
                if (this._minLength && this._minLength == 1) {
                    myValidators.push(Validators.min(1));
                    this._min = 1;
                } 
                else {
                    myValidators.push(Validators.min(0));
                    this._min = 0;
                }

                if (this._maxLength) {
                    this._max = Math.pow(10, Number(this._maxLength)) - 1;
                    myValidators.push(Validators.max(this._max))
                }
                myValidators.push(Validators.pattern(/^[0-9]+$/));
                break;
            case InputType.AlphaNumeric:
                this._type = 'text';
                myValidators.push(Validators.pattern(/^(?:[a-zA-Z0-9\s\&()\-,:./\\]+)?$/));
                break;
            case InputType.PinCode:
                this._type = 'text';
                myValidators.push(Validators.pattern(/^(?:[0-9]+)?$/));
                break;
            case InputType.CaseReference:
                this._type = 'text';
                myValidators.push(Validators.pattern(/^(?:[a-zA-Z0-9]+)?$/));
                this._transform = TransformType.UpperCase;
                break;
            case InputType.Postcode:
                this._transform = TransformType.UpperCaseNoSpaces;
                myValidators.push(Validators.pattern(/^(([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2}))\b$/));
                break;
            case InputType.VehicleRegistration:
                this._minLength = 1;
                this._maxLength = 14;
                myValidators.push(Validators.minLength(this._minLength));
                this._transform = TransformType.UpperCase;
                myValidators.push(Validators.pattern(/^(?:[a-zA-Z0-9\s]+)?$/));
                break;
            case InputType.Decimal:
                this._type = 'number';
                this._step = '0.01';
                myValidators.push(Validators.pattern(/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$/));
                break;
            case InputType.NiNumber:
                myValidators.push(Validators.pattern(/^\s*[a-zA-Z]{2}(?:\s*\d\s*){6}[a-dA-D]{1}\s*$/));
                this._transform = TransformType.UpperCase;
                this._minLength = 9;
                this._maxLength = 9;
                break;
            case InputType.Name:
                myValidators.push(Validators.pattern(/^[A-Za-z\s'-]+$/));
                this._transform = TransformType.Title;
                break;
            case InputType.AddressLine:
                myValidators.push(Validators.pattern(/^[A-Za-z0-9\s'-]+$/));
                this._transform = TransformType.Title;
                break;
            case InputType.Email:
                this._type = 'text';
                myValidators.push(Validators.email);
                break;
        }

        if (control) {
            let validators = control.validator ? [control.validator, ...myValidators] : myValidators;
            
            control.setValidators(validators)
            control.updateValueAndValidity();
        }
    }

    onBlur(): void {
        if (this._value) {
            if (this._transform == TransformType.UpperCase) {
                this._value = this._value.toUpperCase();
                this._propagateChange(this._value);
            }

            if (this._transform == TransformType.UpperCaseNoSpaces) {
                this._value = this._value.replace(/ /g, '').toUpperCase();
                this._propagateChange(this._value);
            }

            if (this._transform == TransformType.Title) {
                this._value = this.toTitleCase(this._value);
                this._propagateChange(this._value);
            }

            if (this._inputType == InputType.Email) {
                if (this._matInput!.valid) {
                    this.lookUpService.checkEmail(this._value).subscribe(result => {
                        if (result.isValid) {
                            this.toastr.info(`${this._value} is a valid email address`);
                        } else {
                            this.toastr.error(`${this._value} is not a valid email address according to our ...`);
                        }
                    })
                }
            }
        }

        this.$onBlur.emit();
    }

    toTitleCase(phrase: string) {
        if (phrase) {
            return phrase.toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
                .trim();
        }

        return phrase;
    };

    valueUpdated(newValue: any) {
        this._value = newValue;
        this._propagateChange(this._value);

        this.controlDir.errors

        let currentLength = Number(this._value);

        if (this.controlDir?.errors?.['maxlength']) {
            this._labelData.validationErrorText = `The ${this._label} has maximum of ${this._maxLength} characters`;
        } else if (this.controlDir?.errors?.['minlength']) {
            this._labelData.validationErrorText = `The ${this._label} has minimum of ${this._minLength} characters`;
        } else if (this._min && currentLength < this._min) {
            this._labelData.validationErrorText = `The ${this._label} has minimum value of ${this._min}`;
        } else if (this._inputType == InputType.AddressLine && this.getHasPatternErrors()) {
            this._labelData.validationErrorText = `Invalid format or invalid characters`;
        } else if (this._inputType != InputType.NiNumber && this.getHasPatternErrors()) {
            this._labelData.validationErrorText = `Only letters, ' and - are allowed`;
        } else if (this.controlDir?.errors?.['profanity']) {
            this._labelData.validationErrorText = 'Contains a naughty word!';
        } else {
            this._labelData.validationErrorText = this._errorText;
        }

        this._showValidationError = this.addClassInvalid && this.controlDir.control!.dirty;
    }

    getHasPatternErrors(): boolean {
        if (this.controlDir.errors) {
            return Object.keys(this.controlDir.errors!).some(x => x == 'pattern');
        }

        return false;
    }

    get addClassRequired(): boolean {
        return this._required && !this._value
    }

    get addClassInvalid(): boolean {
        return this.controlDir.control?.errors != null && !this.addClassRequired;
    }

    override markAsTouched(): void {
        this._matInput!.markAsTouched();
    }
}
